/* global approvalStatuses displayApprovalStatuses */

import React, { useState, useEffect, useRef } from 'react';

import { Modal } from 'flowbite';
import moment from 'moment';

import PurchaseRequisitionsViewModal from '../purchase_requisitions/PurchaseRequisitionsViewModal';

import PurchaseOrdersViewModal from './PurchaseOrdersViewModal';

import { formatFigure } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import useApiRequest from 'Hooks/useApiRequest';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import EyeIcon from 'Assets/icons/eye.svg';

export default function PurchaseOrdersIndexTable(props) {
    const [detailedPOs, setDetailedPOs] = useState({});

    const [viewedPO, setViewedPO] = useState({});
    const [deletedPO, setDeletedPO] = useState({});
    const [canceledPO, setCanceledPO] = useState({});

    const [purchaseOrdersViewModalElement, setPurchaseOrdersViewModalElement] = useState(null);
    const [purchaseOrdersCancelModalElement, setPurchaseOrdersCancelModalElement] = useState(null);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [filters, setFilters] = useState({ search: '' });

    const purchaseOrdersViewModalRef = useRef();
    const purchaseOrdersCancelModalRef = useRef();

    const toast = useToast();

    const pagination = usePagination({ perPage: 25 });

    const fetchPurchaseOrdersRequest = useApiRequest({ url: props.indexPOUrl, errorMessage: $t('An error occurred while fetching Expenditure Approvals') });

    useEffect(() => {
        purchaseOrdersViewModalRef && setPurchaseOrdersViewModalElement(new Modal(purchaseOrdersViewModalRef.current, modalOptions(false)));
        purchaseOrdersCancelModalRef && setPurchaseOrdersCancelModalElement(new Modal(purchaseOrdersCancelModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        fetchPurchaseOrdersRequest.get({ per_page: pagination.perPage, page: pagination.page, search: filters.search });
    }, [pagination.page, pagination.perPage, filters.search]);

    useEffect(() => {
        if (props.refetch) {
            pagination.setPage(1);
        }
    }, [props.refetch]);

    useEffect(() => {
        if (Object.keys(viewedPO).length > 0) {
            purchaseOrdersViewModalElement && purchaseOrdersViewModalElement.show();
        }
    }, [viewedPO]);

    useEffect(() => {
        if (Object.keys(deletedPO).length > 0) {
            //$('#delete-po').modal('show');
        }
    }, [deletedPO]);

    useEffect(() => {
        if (Object.keys(canceledPO).length > 0) {
            purchaseOrdersCancelModalElement && purchaseOrdersCancelModalElement.show();
        }
    }, [canceledPO]);

    useEffect(() => {
        if (fetchPurchaseOrdersRequest.data) {
            const { data, meta } = fetchPurchaseOrdersRequest.data;
            pagination.setTotal(meta.total);
            pagination.setLastPage(meta.last_page);
            pagination.setFrom(meta.from);
            pagination.setTo(meta.to);

            setDetailedPOs((detailedData) =>
                data.reduce((acc, po) => {
                    acc[po.id] = detailedData[po.id] || null;
                    return acc;
                }, {}),
            );
        }
    }, [fetchPurchaseOrdersRequest.data]);

    const fetchPurchaseOrder = (po) => {
        axios
            .get(po.showUrl)
            .then(({ data }) => {
                setViewedPO({ ...data.po });
            })
            .catch((e) => {
                toast.error($t('An error occurred while fetching Expenditure Approvals'));
            });
    };

    const fetchPurchaseOrderDetails = (po) => {
        axios
            .get(po.detailsUrl)
            .then(({ data }) => {
                const { po } = data;
                const detailedData = JSON.parse(JSON.stringify(detailedPOs));
                detailedData[po.id] = { ...po, approval_history: data.approval_history || data.approvers || [] };

                setDetailedPOs(detailedData);
            })
            .catch((e) => {
                toast.error($t('An error occurred while fetching Expenditure Approvals'));
            });
    };

    const toggleShowDetails = (po) => {
        const detailed = JSON.parse(JSON.stringify(detailedPOs));

        if (detailed[po.id]) {
            detailed[po.id] = null;
            setDetailedPOs(detailed);
        } else {
            fetchPurchaseOrderDetails(po);
        }
    };

    const viewPO = (po) => {
        fetchPurchaseOrder(po);
    };

    const cancelPO = (po) => {
        setCanceledPO({ ...po });
    };

    const successHandler = () => {
        setDeletedPO({});
        setViewedPO({});
        setCanceledPO({});
        props.refetchAllHandler();
        purchaseOrdersViewModalElement && purchaseOrdersViewModalElement.hide();
        purchaseOrdersCancelModalElement && purchaseOrdersCancelModalElement.hide();
    };

    const closeModal = () => {
        setDeletedPO({});
        setViewedPO({});
        setCanceledPO({});
        purchaseOrdersViewModalElement && purchaseOrdersViewModalElement.hide();
        purchaseOrdersCancelModalElement && purchaseOrdersCancelModalElement.hide();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerCancelPO = () => {
        setActionInProgress(true);
        axios
            .put(props.cancelUrl.replace('ORDER_ID', canceledPO.id))
            .then((response) => {
                setActionCompleted(true);
                setActionFailed(false);
            })
            .catch((e) => {
                setActionFailed(true);
            });
    };

    const cancelPoConfirmationModalMsg = (selectedCanceledPO) => {
        if (selectedCanceledPO?.purchase_requisition) {
            return $t('Cancel Expenditure Approvals of Purchase Requisition') + ' [' + selectedCanceledPO?.purchase_requisition.pr_code + ']';
        }
        if (!selectedCanceledPO?.purchase_requisition) {
            return $t('Cancel the Expenditure Approvals');
        }
        return '';
    };

    return (
        <div>
            <div className="tw-flex tw-justify-between tw-mb-4">
                <div>
                    <h4 className="table__header">{props.heading}</h4>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => {
                                pagination.setPage(+data.selected + 1);
                            }}
                            forcePage={pagination.page - 1}
                        />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2">
                    <div>
                        <PerPageSelect
                            value={pagination.perPage}
                            onChange={(e) => {
                                pagination.setPerPage(parseInt(e.target.value));
                            }}
                        />
                    </div>
                    <div>
                        <TableSearchInput searchValue={filters.search} handleEnterPress={(e, value) => setFilters((prev) => ({ ...prev, search: value }))} />
                    </div>
                </div>
            </div>

            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th className="tw-min-w-64 tw-sticky tw-left-0 tw-h-fit">
                                <span className="tw-block">{$t('Procurement Name')}</span>
                            </th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Unique ID')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Owner')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Value')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Procurement method')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Supplier')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Requested By')}</th>
                            <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Requested At')}</th>
                            <th className="tw-w-44 tw-min-w-44 tw-max-w-44">{$t('Approved By')}</th>
                            <th className="tw-w-28 tw-min-w-28 tw-max-w-28">{$t('Approved At')}</th>
                            <th className="tw-w-32 tw-min-w-32 tw-max-w-32">{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fetchPurchaseOrdersRequest.loading ? (
                            <TableRowSkeleton rows={10} columns={11} fixed={true} />
                        ) : (
                            fetchPurchaseOrdersRequest.data?.data?.map((po) => {
                                return (
                                    <React.Fragment key={po.id + po.status_label}>
                                        <tr key={po.id} className="tw-cursor-pointer">
                                            <td
                                                className="tw-sticky tw-left-0 tw-h-fit tw-text-wrap"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                                    <div className="tw-font-semibold">{po.procurement_plan_item_name}</div>
                                                    <div>{detailedPOs[po.id] ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                                                </div>
                                            </td>
                                            <td
                                                className="tw-min-w-44"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.code ? po.code : $t(props.typeFormatted)}
                                            </td>
                                            <td
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.owner_name}
                                            </td>
                                            <td
                                                className="inbud-numeric-cell"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {formatFigure(po.final_price)}
                                            </td>
                                            <td
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.procurement_policy_method}
                                            </td>
                                            <td
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.supplier_name || ''}
                                            </td>
                                            <td
                                                className="tw-text-nowrap"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.requestor_name || ''}
                                            </td>
                                            <td
                                                className="tw-text-nowrap inbud-date-cell"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.requested_at}
                                            </td>
                                            <td
                                                className="tw-truncate"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                <Tooltip placement="bottom" tooltipContent={po.approver_name || ''} tooltipType="text">
                                                    <span>{po.approver_name || ''}</span>
                                                </Tooltip>
                                            </td>
                                            <td
                                                className="tw-text-nowrap inbud-date-cell"
                                                onClick={(e) => {
                                                    toggleShowDetails(po);
                                                }}
                                            >
                                                {po.approved_at}
                                            </td>
                                            <td className="tw-p-4">
                                                <div className="tw-flex tw-justify-end tw-gap-x-3">
                                                    <TextButton variant="primary" onClick={() => viewPO(po)}>
                                                        <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View')}
                                                    </TextButton>
                                                </div>
                                            </td>
                                        </tr>
                                        {!!detailedPOs[po.id] && (
                                            <tr>
                                                <td className="tw-sticky tw-left-0 tw-h-fit "></td>
                                                <td className="tw-px-4 tw-py-5 !tw-bg-blue-25/50" colSpan="10">
                                                    <PurchaseOrderDetails po={detailedPOs[po.id]} approvalStatuses={approvalStatuses} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <PurchaseOrdersViewModal
                modalId={'view-po'}
                modalRef={purchaseOrdersViewModalRef}
                modalElement={purchaseOrdersViewModalElement}
                closeHandler={closeModal}
                successHandler={successHandler}
                po={viewedPO}
                displayApprovalStatuses={displayApprovalStatuses}
            />
            <ConfirmationModal
                closeHandler={closeModal}
                successHandler={successHandler}
                triggerAction={triggerCancelPO}
                cleanStates={cleanConfirmationModalStates}
                allowAction={Object.keys(canceledPO).length > 0}
                modalId={'cancel-po'}
                modalRef={purchaseOrdersCancelModalRef}
                modalElement={purchaseOrdersCancelModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                warningMsg={$t(
                    'Deleting a committed expense will also delete the budget held for this commitment. You will need to raise the budget expense line again if you still require these items',
                )}
                confirmationMsg={cancelPoConfirmationModalMsg(canceledPO)}
                actionCompletedMsg={$t('Expenditure Approvals canceled')}
                actionFailedMsg={$t('Expenditure Approvals could not be canceled')}
                title={$t('Cancellation of Expenditure Approvals')}
                cancelButtonFirst={true}
                submitButtonVariant="primary"
                cancelButtonVariant="danger"
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Confirm')}
            />
        </div>
    );
}

function PurchaseOrderDetails({ po, approvalStatuses }) {
    const [purchaseRequisitionsViewModalElement, setPurchaseRequisitionsViewModalElement] = useState(null);
    const [viewedPR, setViewedPR] = useState({});

    const purchaseRequisitionsViewModalRef = useRef();

    useEffect(() => {
        const timer = setTimeout(() => {
            if (purchaseRequisitionsViewModalRef.current) {
                setPurchaseRequisitionsViewModalElement(new Modal(purchaseRequisitionsViewModalRef.current, modalOptions(false)));
            }
        }, 0);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (Object.keys(viewedPR).length > 0 && purchaseRequisitionsViewModalElement) {
            purchaseRequisitionsViewModalElement.show();
        }
    }, [viewedPR, purchaseRequisitionsViewModalElement]);

    const viewPR = (pr) => {
        setViewedPR({ ...pr });
    };

    const successHandler = () => {
        setViewedPR({});
        purchaseRequisitionsViewModalElement && purchaseRequisitionsViewModalElement.hide();
    };

    const closeModal = () => {
        setViewedPR({});
        purchaseRequisitionsViewModalElement && purchaseRequisitionsViewModalElement.hide();
    };

    return (
        <React.Fragment>
            {!!po.purchase_requisition && (
                <React.Fragment>
                    <h5 className="tw-text-blue tw-font-extrabold tw-uppercase tw-mb-2.5">{$t('Purchase Requisition Details')}</h5>
                    {po.description && (
                        <p className="tw-text-sm tw-mb-2.5">
                            <span className="tw-font-semibold">{$t('Description')}:</span> {po.description}
                        </p>
                    )}
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    <th className="tw-w-24 tw-min-w-24 tw-max-w-24 !tw-bg-charcoal-25">{$t('Unique ID')}</th>
                                    <th className="tw-min-w-44 !tw-bg-charcoal-25">{$t('Title')}</th>
                                    <th className="tw-w-44 tw-min-w-44 tw-max-w-44 !tw-bg-charcoal-25">{$t('Value')}</th>
                                    <th className="tw-w-44 tw-min-w-44 tw-max-w-44 !tw-bg-charcoal-25">{$t('Procurement method')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28 !tw-bg-charcoal-25">{$t('Target Award')}</th>
                                    <th className="tw-w-44 tw-min-w-44 tw-max-w-44 !tw-bg-charcoal-25">{$t('Status')}</th>
                                    <th className="tw-w-44 tw-min-w-44 tw-max-w-44 !tw-bg-charcoal-25">{$t('Approved By')}</th>
                                    <th className="tw-w-28 tw-min-w-28 tw-max-w-28 !tw-bg-charcoal-25">{$t('Approved At')}</th>
                                    <th className="!tw-bg-charcoal-25">{$t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="tw-bg-[#f6f6f6]">
                                    <td>{po.purchase_requisition.pr_code}</td>
                                    <td>{po.purchase_requisition.title}</td>
                                    <td className="inbud-numeric-cell">{formatFigure(po.purchase_requisition.estimated_value)}</td>
                                    <td>{po.purchase_requisition.procurement_policy_method || '-'}</td>
                                    <td className="inbud-date-cell">{moment(po.purchase_requisition.contract_date).format('DD-MM-YYYY')}</td>
                                    <td>{po.purchase_requisition.status}</td>
                                    <td>{po.purchase_requisition.approved_by ? po.purchase_requisition.approved_by : '-'}</td>
                                    <td className="inbud-date-cell">
                                        {po.purchase_requisition.approved_at ? moment(po.purchase_requisition.approved_at).format('DD-MM-YYYY') : '-'}
                                    </td>
                                    <td className="tw-p-4">
                                        <TextButton variant="primary" onClick={() => viewPR(po.purchase_requisition)}>
                                            <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View')}
                                        </TextButton>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <PurchaseRequisitionsViewModal
                            modalId={'view-pr'}
                            modalRef={purchaseRequisitionsViewModalRef}
                            modalElement={purchaseRequisitionsViewModalElement}
                            closeHandler={closeModal}
                            successHandler={successHandler}
                            pr={viewedPR}
                            displayApprovalStatuses={displayApprovalStatuses}
                        />
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    );
}
